import { useAuth } from '@mdvip/shared/contexts/AuthContext';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface AuthCallbackPageProps {
  /** Path to navigate to after successful auth. Each portal supplies its own post-login landing route. */
  postLoginRedirect: string;
}

export function AuthCallbackPage({ postLoginRedirect }: AuthCallbackPageProps) {
  const { t } = useTranslation('shared');
  const navigate = useNavigate();
  const { handleAuthCallback, initiateLogin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Ref guard prevents double-invocation in React Strict Mode, which mounts
  // effects twice in development. Without this, the code would be exchanged
  // twice and the second call would fail with an already-used code error.
  const exchangeCalledRef = useRef(false);

  useEffect(() => {
    if (exchangeCalledRef.current) return;
    exchangeCalledRef.current = true;

    const params = new URLSearchParams(window.location.search);

    // The backend redirects here with ?error= when SAML authentication fails
    // (e.g. ?error=saml_failed&detail=SAML+provider+returned+AuthnFailed+...).
    const errorCode = params.get('error');
    if (errorCode) {
      const detail = params.get('detail');
      setError(detail ?? `Authentication failed (${errorCode}). Please try signing in again.`);
      return;
    }

    // The SAML IdP appends ?code= to the callbackUrl after authentication.
    const code = params.get('code');

    if (!code) {
      setError(t('authCallback.noCode'));
      return;
    }

    handleAuthCallback(code)
      .then(() => {
        // replace: true removes the callback URL from history so the back button
        // doesn't re-trigger the exchange with an already-consumed code.
        navigate(postLoginRedirect, { replace: true });
      })
      .catch((err) => {
        console.error('Auth callback failed:', err);
        setError(t('authCallback.callbackFailed'));
      });
  }, [handleAuthCallback, navigate, postLoginRedirect, t]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <p className="mb-4 text-sm text-destructive">{error}</p>
          <button type="button" onClick={() => void initiateLogin()} className="text-sm text-primary underline">
            {t('authCallback.returnToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{t('authCallback.completing')}</p>
      </div>
    </div>
  );
}
